import { useMemo } from 'react'

import { subDays } from 'date-fns'

import { getPreviousPeriodDateRange } from '@buffer-mono/date-utils'

import {
  useAccount,
  useChannelsConnected,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { useAllChannelsInsightsSummary } from '../../AllChannelsInsights/hooks/useAllChannelsInsightsSummary'
import type { SummaryMetric } from '../../AllChannelsInsights/hooks/useAllChannelsInsightsSummary'
import { metricToStat } from '../SummaryMetrics/metricToStat'
import type { InsightsSnapshotProps } from '../InsightsSnapshot'
import { buildAvatarProxyUrl } from '../../summaryCardToImage'
import { comparisonShortLabel, formatDateRange } from '../../utils'

/** Trailing window (inclusive days) the announcement snapshot summarizes. */
const SNAPSHOT_WINDOW_DAYS = 30

/**
 * The four metrics the announcement snapshot shows, in tile order. All four are
 * baseline metrics the all-channels summary always returns, so a fixed
 * selection (rather than `pickShareableStats`, which curates by reach/network)
 * gives a stable, on-brand scorecard.
 */
const SNAPSHOT_METRIC_TYPES: Array<SummaryMetric['type']> = [
  'reactions',
  'comments',
  'postCount',
  'followers',
]

export type SnapshotStatus = 'loading' | 'personalized' | 'fallback'

export interface PersonalizedInsightsSnapshot {
  /**
   * `loading` while prerequisites/data resolve, `personalized` when we have at
   * least one non-zero metric to show, `fallback` for every no-data case
   * (no channels connected, never-synced/empty summary, or all-zero data).
   */
  status: SnapshotStatus
  /**
   * Props for the branded `<InsightsSnapshot>` — the very same 1080×1080 card
   * the "Share as Post" button generates. `null` unless `personalized`.
   */
  snapshot: InsightsSnapshotProps | null
}

/**
 * Gathers a personalized, all-channels Insights snapshot for the announcement
 * dialog, reusing the exact `InsightsSnapshot` the "Share as Post" flow renders
 * so the announcement previews the shareable card itself.
 *
 * The summary query is skipped unless `skip` is false, so metrics are only
 * fetched for the targeted, non-dismissed audience.
 *
 * `channelIds` is intentionally omitted from the query: the all-channels use
 * case authorizes per-channel server-side, so the aggregate is already scoped
 * to the channels the actor can view without needing channel type / allowed
 * actions on the root channel objects. A previous-period comparison is
 * requested so the snapshot tiles carry trend deltas, matching the share image.
 */
export function usePersonalizedInsightsSnapshot({
  skip,
}: {
  skip: boolean
}): PersonalizedInsightsSnapshot {
  const organizationId = useOrganizationId()
  const channels = useChannelsConnected()
  const { account } = useAccount()

  // Compute the range once per mount so query variables stay stable.
  const { startDate, endDate, comparisonStartDate, comparisonEndDate } =
    useMemo(() => {
      const end = new Date()
      const start = subDays(end, SNAPSHOT_WINDOW_DAYS - 1)
      const { previousStartDate, previousEndDate } = getPreviousPeriodDateRange(
        start,
        end,
      )
      return {
        startDate: start,
        endDate: end,
        comparisonStartDate: previousStartDate,
        comparisonEndDate: previousEndDate,
      }
    }, [])

  const { summary, loading, error } = useAllChannelsInsightsSummary({
    organizationId: organizationId ?? '',
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
    skip: skip || !organizationId,
  })

  const picked = useMemo(
    () =>
      SNAPSHOT_METRIC_TYPES.map((type) =>
        summary.find((metric) => metric.type === type),
      ).filter((metric): metric is SummaryMetric => metric != null),
    [summary],
  )

  // A never-synced channel still returns the baseline metrics at 0, so an
  // explicit non-zero check is what distinguishes real data from the all-zero
  // fallback case.
  const hasData = picked.some((metric) => metric.value > 0)

  const status: SnapshotStatus =
    skip || loading
      ? 'loading'
      : error || channels.length === 0 || !hasData
        ? 'fallback'
        : 'personalized'

  const snapshot = useMemo<InsightsSnapshotProps | null>(() => {
    if (status !== 'personalized') return null
    const channelCount = channels.length
    return {
      stats: picked.map(metricToStat),
      dateRangeLabel: formatDateRange(startDate, endDate),
      channelCountLabel: `${channelCount} ${channelCount === 1 ? 'channel' : 'channels'}`,
      comparisonLabel: comparisonShortLabel('previous-period'),
      channelAvatarUrl: buildAvatarProxyUrl(account?.avatar ?? undefined),
    }
  }, [status, picked, startDate, endDate, channels.length, account?.avatar])

  return { status, snapshot }
}
